/**
 * wizardState.js — RC 1.41 Phase D
 *
 * Tiny sessionStorage-backed state machine for the Job Application
 * Wizard. Keyed by jobId so multiple jobs each have their own
 * resume-on-refresh state.
 *
 * Schema versioning: bump SCHEMA_VERSION to invalidate stale state
 * when the wizard contract changes.
 */

const SCHEMA_VERSION = 1;
const KEY_PREFIX = 'ndor-wizard:';

export const WIZARD_STEPS = ['job', 'cv', 'letter', 'review'];

function key(jobId) { return `${KEY_PREFIX}${jobId}`; }

export function loadWizardState(jobId) {
  if (!jobId) return null;
  try {
    const raw = sessionStorage.getItem(key(jobId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== SCHEMA_VERSION) return null;
    return parsed;
  } catch { return null; }
}

export function saveWizardState(jobId, partial) {
  if (!jobId) return;
  const existing = loadWizardState(jobId) || {};
  const next = { v: SCHEMA_VERSION, jobId, ...existing, ...partial };
  try { sessionStorage.setItem(key(jobId), JSON.stringify(next)); } catch { /* */ }
}

export function clearWizardState(jobId) {
  if (!jobId) return;
  try { sessionStorage.removeItem(key(jobId)); } catch { /* */ }
}

// RC 1.52 Phase 3A.DUP — system-name minting via shared helper.
// Pack names are SYSTEM-generated and collision-proof. Inputs are
// preserved for back-compat with snapshot-restore but the helper
// always returns a fresh uncollidable stamp; jobTitle/company are
// no longer embedded in the name.
import { mintApplicationPackName } from './titleStamps';

export function defaultPackName(_jobTitle, _company) {
  return mintApplicationPackName();
}

// ─── RC 1.49.4 L-1 (operator-locked, 2026-05-08) ───────────────────
// Wizard "is currently open for jobId X" marker.
//
// Stored in `localStorage` (NOT sessionStorage) so it survives a
// full browser close + reopen — matching the operator directive
// that an active wizard session for a job should auto-reopen on
// remount even after a hard refresh or app restart. The marker is
// CLEARED in two situations:
//   • The user finishes the pack (success → wizard closes happily).
//   • The user closes the wizard via the cancel-warning "Close
//     anyway" button (or the dialog close path when no work is in
//     flight — see JobApplicationWizard.jsx onOpenChange guard).
//
// Stale-marker safety: a 24h TTL guards against a marker leaking
// from a session the user genuinely abandoned days ago. Long enough
// for "I'll come back tomorrow" UX without becoming permanent.
const WIZARD_OPEN_KEY_PREFIX = 'ndor-wizard-open:';
const WIZARD_OPEN_TTL_MS = 24 * 60 * 60 * 1000;

function _wizardOpenKey(jobId) { return `${WIZARD_OPEN_KEY_PREFIX}${jobId}`; }

export function markWizardOpen(jobId) {
  if (!jobId) return;
  try {
    localStorage.setItem(
      _wizardOpenKey(jobId),
      JSON.stringify({ markedAtMs: Date.now() }),
    );
  } catch { /* private-mode / quota — non-fatal */ }
}

export function clearWizardOpen(jobId) {
  if (!jobId) return;
  try { localStorage.removeItem(_wizardOpenKey(jobId)); } catch { /* */ }
}

export function isWizardOpen(jobId) {
  if (!jobId) return false;
  try {
    const raw = localStorage.getItem(_wizardOpenKey(jobId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.markedAtMs !== 'number') return false;
    if ((Date.now() - parsed.markedAtMs) > WIZARD_OPEN_TTL_MS) {
      // Stale — purge so we don't keep returning true.
      try { localStorage.removeItem(_wizardOpenKey(jobId)); } catch { /* */ }
      return false;
    }
    return true;
  } catch { return false; }
}
